import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { findTool } from '../data/calculators';
import { ArrowLeft, Wrench } from 'lucide-react';
import { CALC_REGISTRY } from '../calculators/registry';
import { getSeoForSlug } from '../data/seoMap';

export default function CalculatorPage() {
  const { slug } = useParams();
  const tool = findTool(slug);
  const Component = CALC_REGISTRY[slug];

  // Scroll to top whenever a new calculator is opened so users always see the tool from its title.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    // Track recent + popular calculators in localStorage for the Home dashboard
    if (slug && tool) {
      try {
        const RECENT_KEY = 'calc_recent';
        const POPULAR_KEY = 'calc_popular';
        const recent = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter(s => s !== slug);
        recent.unshift(slug);
        localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 12)));
        const popular = JSON.parse(localStorage.getItem(POPULAR_KEY) || '{}');
        popular[slug] = (popular[slug] || 0) + 1;
        localStorage.setItem(POPULAR_KEY, JSON.stringify(popular));
      } catch {}
    }
  }, [slug, tool]);

  // SEO meta tags — dynamic per calculator
  useEffect(() => {
    if (!tool) return;
    const seo = getSeoForSlug(slug);
    const title = seo?.title || `${tool.name} — Free Online ${tool.categoryName || 'Calculator'} | PocketCalculator.net`;
    const desc = seo?.desc || `Free ${tool.name.toLowerCase()} — quick, accurate, no signup. Real-time results, mobile friendly.`;
    const keywords = seo?.keywords || `${tool.name.toLowerCase()}, ${tool.name.toLowerCase()} online, free ${tool.name.toLowerCase()}`;
    document.title = title;
    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', desc);
    setMeta('keywords', keywords);
    setMeta('og:title', tool.name, 'property');
    setMeta('og:description', desc, 'property');
    setMeta('og:type', 'website', 'property');
    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', `https://pocketcalculator.net/calc/${slug}`);
    // FAQ JSON-LD (if defined)
    let faqScript = document.getElementById('calc-faq-jsonld');
    if (seo?.faqs?.length) {
      const jsonld = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: seo.faqs.map(f => ({
          '@type': 'Question', name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a }
        }))
      };
      if (!faqScript) {
        faqScript = document.createElement('script');
        faqScript.type = 'application/ld+json';
        faqScript.id = 'calc-faq-jsonld';
        document.head.appendChild(faqScript);
      }
      faqScript.textContent = JSON.stringify(jsonld);
    } else if (faqScript) {
      faqScript.remove();
    }
    // Breadcrumb JSON-LD
    let bcScript = document.getElementById('calc-breadcrumb-jsonld');
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://pocketcalculator.net/' },
        { '@type': 'ListItem', position: 2, name: tool.categoryName || 'Calculators', item: 'https://pocketcalculator.net/category/' + (tool.category || 'math') },
        { '@type': 'ListItem', position: 3, name: tool.name, item: 'https://pocketcalculator.net/calc/' + slug },
      ],
    };
    if (!bcScript) {
      bcScript = document.createElement('script');
      bcScript.type = 'application/ld+json';
      bcScript.id = 'calc-breadcrumb-jsonld';
      document.head.appendChild(bcScript);
    }
    bcScript.textContent = JSON.stringify(breadcrumb);
    return () => {
      document.title = 'PocketCalculator.net';
      document.getElementById('calc-faq-jsonld')?.remove();
      document.getElementById('calc-breadcrumb-jsonld')?.remove();
    };
  }, [tool, slug]);

  // Alias route — slug is in registry but not in the nav data. Still render.
  if (!tool && Component) {
    return (
      <div>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> All Calculators
        </Link>
        <Component slug={slug} />
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="text-center py-20">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Calculator not found</h1>
        <Link to="/" className="text-blue-600">Go home</Link>
      </div>
    );
  }

  return (
    <div>
      <Link to={`/category/${tool.category}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 mb-4">
        <ArrowLeft className="w-4 h-4" /> {tool.categoryName}
      </Link>

      {Component ? (
        <Component slug={slug} />
      ) : (
        <div className="calc-card">
          <div className="calc-header">
            <h2>{tool.name}</h2>
            <p>This calculator is on our roadmap and will be added in the next iteration.</p>
          </div>
          <div className="calc-body">
            <div className="flex items-center gap-3 text-slate-600">
              <Wrench className="w-5 h-5 text-orange-500" />
              <span>Coming soon — in the meantime, check out other implemented calculators.</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
